import { useState } from "react";
import { FileCheck2, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const inputCls =
  "w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#0284C7]";

const ReceiptForm = () => {
  const [form, setForm] = useState({ prenom: "", nom: "", montant: "", adresse: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/donations/receipt-request", {
        ...form,
        montant: Number(form.montant),
      });
      toast.success(data.message);
      setSent(true);
      setForm({ prenom: "", nom: "", montant: "", adresse: "", email: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Vérifiez les champs du formulaire puis réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="cerfa" data-testid="receipt-form-card" className="card-site mx-auto mt-8 max-w-xl scroll-mt-28 p-6 sm:p-10">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#10B981]">
        <FileCheck2 size={23} />
      </span>
      <h3 className="mt-5 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
        Recevoir mon reçu fiscal
      </h3>
      <p
        data-testid="receipt-cerfa-message"
        className="mt-4 rounded-xl border-l-4 border-[#10B981] bg-[#ECFDF5] px-4 py-3.5 text-sm sm:text-base font-semibold leading-relaxed text-[#065F46]"
      >
        Un reçu fiscal (CERFA 11580) vous sera envoyé par email après réception de votre don,
        vous permettant de déduire 66 % de votre don de vos impôts.
      </p>

      {sent ? (
        <div className="mt-6 text-center" data-testid="receipt-success">
          <CheckCircle2 size={44} className="mx-auto text-[#10B981]" />
          <p className="mt-4 font-heading text-lg font-bold text-[#0F2C59]">
            Demande enregistrée !
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Merci ! Votre reçu fiscal CERFA 11580 vous sera envoyé par e-mail après réception de
            votre don. Pensez à vérifier vos courriers indésirables.
          </p>
          <button
            type="button"
            onClick={() => setSent(false)}
            data-testid="receipt-new-request-button"
            className="btn-outline-navy mt-6 !px-5 !py-2.5 !text-sm"
          >
            Faire une autre demande
          </button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="receipt-form" className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="receipt-prenom" className="mb-1.5 block text-sm font-bold text-slate-700">
                Prénom *
              </label>
              <input
                id="receipt-prenom"
                required
                minLength={2}
                value={form.prenom}
                onChange={update("prenom")}
                data-testid="receipt-firstname-input"
                className={inputCls}
                placeholder="Marie"
              />
            </div>
            <div>
              <label htmlFor="receipt-nom" className="mb-1.5 block text-sm font-bold text-slate-700">
                Nom *
              </label>
              <input
                id="receipt-nom"
                required
                minLength={2}
                value={form.nom}
                onChange={update("nom")}
                data-testid="receipt-lastname-input"
                className={inputCls}
                placeholder="Dupont"
              />
            </div>
          </div>
          <div>
            <label htmlFor="receipt-montant" className="mb-1.5 block text-sm font-bold text-slate-700">
              Montant du don (€) *
            </label>
            <div className="flex items-center rounded-xl border-2 border-slate-200 bg-white px-4 transition-colors focus-within:border-[#0284C7]">
              <input
                id="receipt-montant"
                type="number"
                required
                min="1"
                step="0.01"
                inputMode="decimal"
                value={form.montant}
                onChange={update("montant")}
                data-testid="receipt-amount-input"
                className="w-full bg-transparent py-3 text-base font-semibold text-[#0F2C59] outline-none placeholder:font-normal placeholder:text-slate-400"
                placeholder="Ex. : 50"
              />
              <span className="font-heading text-lg font-extrabold text-[#0F2C59]">€</span>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Indiquez le montant que vous avez donné (PayPal ou virement) : il figurera sur votre reçu.
            </p>
          </div>
          <div>
            <label htmlFor="receipt-adresse" className="mb-1.5 block text-sm font-bold text-slate-700">
              Adresse postale *
            </label>
            <textarea
              id="receipt-adresse"
              required
              minLength={5}
              rows={2}
              value={form.adresse}
              onChange={update("adresse")}
              data-testid="receipt-address-input"
              className={`${inputCls} resize-y`}
              placeholder="12 rue des Lilas, 95200 Sarcelles"
            />
          </div>
          <div>
            <label htmlFor="receipt-email" className="mb-1.5 block text-sm font-bold text-slate-700">
              E-mail *
            </label>
            <input
              id="receipt-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="receipt-email-input"
              className={inputCls}
              placeholder="vous@exemple.fr"
            />
          </div>
          <button type="submit" disabled={loading} data-testid="receipt-form-submit-button" className="btn-don w-full !py-3.5">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            Demander mon reçu fiscal
          </button>
          <p className="text-center text-[11px] leading-relaxed text-slate-400">
            Ces informations servent uniquement à l'établissement de votre reçu fiscal
            (voir notre politique de confidentialité).
          </p>
        </form>
      )}
    </div>
  );
};

export default ReceiptForm;
